import { useQuery } from '@apollo/client';
import { GOOGLE_OAUTH_QUERY } from '@repo-sapiens/graphql';
import {
  Text,
  PageLayout,
  Separator,
  PlainButton,
  LightButton,
} from '../../../../components';
import { MultiplayerWhiteIcon, MultiplayerWhitePlainLogo } from '../../../../components/icons';
import React, { useCallback, useEffect } from 'react';
import { getAccessToken, removeTokens } from '../../../../services';
import { GoogleOauthQueryDto } from '../../../../types';
import { E_Route } from '@repo-sapiens/constants';
import { extractFromQuery } from '@repo-sapiens/helpers';
import ReactGA from 'react-ga4';
import { LoginContainer } from './styles';
import { config } from '../../../../config';
import { trackEvent } from '../../../../libs';
import { css } from '@emotion/css';
import { LoginCarousel } from './components';

type Params = {
  utm_source?: string;
};
export const LoginScreen: React.FC = () => {
  const [source, setSource] = React.useState<string | null>(null);
  const { data } = useQuery<
    {
      googleOAuthUrl: string;
    },
    GoogleOauthQueryDto
  >(GOOGLE_OAUTH_QUERY, {
    variables: {
      oauthData: {
        redirect: `${config.host}${E_Route.AuthContinue}`,
        type: 'login',
        services: [],
        source: source ?? undefined,
      },
    },
  });

  useEffect(() => {
    const { utm_source } = extractFromQuery<Params>(window.location.search);
    if (utm_source) {
      setSource(utm_source);
    }
  }, []);

  const checkAuth = (authWindow: Window | null) => {
    //const openGmail = () => {
    //  window.open("https://mail.google.com/mail/u/0/#inbox", "_blank");
    //};
    const intervalId = setInterval(() => {
      const token = getAccessToken();
      if (token) {
        clearInterval(intervalId);
        if (authWindow) {
          //authWindow.close();
        }
        //openGmail();
        const redirect = localStorage.getItem('redirect');
        if (redirect) {
          window.location.href = redirect;
          localStorage.removeItem('redirect');
        } else {
          window.location.href = E_Route.Home;
        }
        window.close();
      }
    }, 1000);
  };

  const handleGoogleLogin = useCallback(() => {
    if (import.meta.env.PROD) {
      ReactGA.event({
        category: 'Login',
        action: `Login clicked`,
        label: 'Login',
        nonInteraction: false,
        transport: 'xhr',
      });
      trackEvent('Login clicked', {
        source: 'web',
      });
    }
    if (data?.googleOAuthUrl) {
      removeTokens();
      const url = data?.googleOAuthUrl;
      const authWindow = window.open(url, '_blank');
      checkAuth(authWindow);
    }
  }, [data?.googleOAuthUrl]);

  useEffect(() => {
    const { source } = extractFromQuery<{ source: string }>(
      window.location.search
    );
    if (source && source === 'ads') {
      handleGoogleLogin();
    }
  }, [handleGoogleLogin]);

  return (
    <PageLayout title="Login | Multiplayer">
      <LoginContainer>
        <div className={css`
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: flex-start;
          @media (max-width: 825px) {
            border-bottom: 1px solid #3A3A3A;
          }
        `}>
          <div
          className={css`
            width: 100%;
            height: 100%;
            padding: 80px 60px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            position: relative;
            @media (max-width: 825px) {
              padding: 32px 12px;
              align-items: center;
            }
          `}
        >
          <a href="https://multiplayer.work" target="_blank" rel="noreferrer">
            <div
              className={css`
                box-shadow: 0px 6.667px 6.667px 0px rgba(0, 0, 0, 0.25);
                position: absolute;
                top: 80px;
                left: calc(50% - 170px);
                @media (max-width: 825px) {
                  position: relative;
                  top: 0;
                  left: 0;
                  margin-bottom: 32px;
                }
              `}
            >
              <MultiplayerWhitePlainLogo
                style={{ width: '72px', height: '72px' }}
              />
            </div>
          </a>
          <div>
          <Separator.Horizontal spacing={12} className={css`
            @media (max-width: 825px) {
              display: none;
            }
          `} />
          <Text.H2>
            Welcome to Multiplayer
          </Text.H2>
          <Separator.Horizontal spacing={6} />
          <Text.BodySM color="seconday">
            Sign up to start using Multiplayer
          </Text.BodySM>
          <Separator.Horizontal spacing={4} />
          {/* <GoogleSignInBtn onClick={handleGoogleLogin} /> */}

          <PlainButton onClick={handleGoogleLogin} text="Sign up" width="90%" />

<Separator.Horizontal spacing={8} />
            <Text.BodySM color="seconday">
              Already have an account? Log back in
          </Text.BodySM>
          <Separator.Horizontal spacing={1} />

          <LightButton onClick={handleGoogleLogin} text="Log back in" width="90%" />
          </div>
          {/* <button
            onClick={handleGoogleLogin}
            className={css`
              border-radius: 76px;
              background: ${theme.colors.text['highlight/3']};
              max-width: 360px;
              width: 100%;
              height: 48px;
              cursor: pointer;
              color: ${theme.colors.text.dark};

              font-family: ${theme.font.button.fontFamily};
              font-size: ${theme.font.button.fontSize};
              font-style: normal;
              font-weight: 900;
              display: flex;
              justify-content: center;
              align-items: center;
            `}
          >
            <img
              src={GoogleIcon}
              alt="Google Icon"
              style={{ width: '24px', height: '24px', marginRight: '16px' }}
            />
            Login
          </button> */}
          </div>

          <div
            className={css`
              position: absolute;
              bottom: 40px;
              padding: 0px 24px;
              @media (max-width: 1024px) {
                display: none;
              }
            `}
          >
            <Text.BodyXS>
              By proceeding, you agree to the{' '}
              <Text.Link
                size="body-xs"
                target="_blank"
                rel="noreferrer"
                href="https://www.multiplayer.work/terms-of-service"
              >
                Terms of Service
              </Text.Link>{' '}
              and{' '}
              <Text.Link
                size="body-xs"
                target="_blank"
                rel="noreferrer"
                href="https://www.multiplayer.work/privacy-policy"
              >
                Privacy Policy
              </Text.Link>
            </Text.BodyXS>
          </div>
        </div>

        <div className={css`
          width: 100%;
          height: 100%;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          border-left: 1px solid #3A3A3A;
        `}>
          <LoginCarousel />
        </div>
      </LoginContainer>
    </PageLayout>
  );
};
